// src/pages/GeneratedCV.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import "../styles/GeneratedCV.css";
import logo from "../assets/logo1.png";
import 'bootstrap-icons/font/bootstrap-icons.css';

// React-PDF
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  pdf
} from "@react-pdf/renderer";

// ================== Estilos PDF ==================
const pdfStyles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    fontFamily: "Helvetica",
    position: "relative"
  },
  watermark: {
    position: "absolute",
    top: "40%",
    left: "25%",
    fontSize: 60,
    color: "rgba(200,200,200,0.2)",
    transform: "rotate(-30deg)"
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    borderBottom: "1px solid #ccc",
    paddingBottom: 10
  },
  logo: { width: 60, height: 60 },
  title: { fontSize: 18, textAlign: "center", marginBottom: 20 },
  section: { marginBottom: 12 },
  subtitle: { fontSize: 14, marginBottom: 6, fontWeight: "bold" },
  text: { marginBottom: 4 },

  // === Estilos para bloques de entrevista ===
  qaItem: {
    marginBottom: 8,
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 4,
    backgroundColor: "#fafafa"
  },
  qaLabel: { fontSize: 11, fontWeight: "bold" },
  qaText: { fontSize: 11, marginBottom: 3, lineHeight: 1.3 },
  qaMeta: { fontSize: 10, marginBottom: 3, color: "#555" },
  scoreTag: {
    fontSize: 10,
    alignSelf: "flex-start",
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 6,
    paddingRight: 6,
    borderRadius: 3,
    backgroundColor: "#eef2ff",
    marginTop: 2
  }
});

// ================== Helpers ==================
const getField = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const val = obj?.[k];
    if (val !== undefined && val !== null && String(val).trim() !== "") return val;
  }
  return fallback;
};

const fetchInterviewData = async (token) => {
  try {
    const r = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/users/interview-responses`,
      { headers: { Authorization: token } }
    );
    // Esperado: { score, analysis: [...], questions: [...], responses: [...] }
    return r.data || null;
  } catch {
    return null;
  }
};

// ================== Documento PDF ==================
const CVDocument = ({ cv, extras }) => {
  // Tomar entrevista enriquecida (si existe)
  const interview = extras?.interview || {};

  // Arreglos seguros (compatibles con diferentes estructuras)
  const analysisArr = Array.isArray(interview.analysis)
    ? interview.analysis
    : (Array.isArray(extras?.interviewAnalysis?.analysis)
      ? extras.interviewAnalysis.analysis
      : (Array.isArray(extras?.interviewAnalysis)
        ? extras.interviewAnalysis
        : []));

  const questionsArr =
    interview.questions ||
    extras?.interviewQuestions ||
    extras?.interviewAnalysis?.questions ||
    [];

  const responsesArr =
    interview.responses ||
    extras?.interviewResponses ||
    [];

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Marca de agua */}
        <Text style={pdfStyles.watermark}>MIRAI</Text>

        {/* Encabezado con logo */}
        <View style={pdfStyles.header}>
          <Image style={pdfStyles.logo} src={logo} />
          <Text style={{ fontSize: 16, fontWeight: "bold" }}>Currículum Profesional</Text>
        </View>

        {/* CV principal */}
        <View style={pdfStyles.section}>
          <Text>{cv}</Text>
        </View>

        {/* Soft skills */}
        {extras.softSkillsResults && extras.softSkillsResults.results && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.subtitle}>Habilidades Blandas Destacadas</Text>
            {Object.entries(extras.softSkillsResults.results).map(([skill, data], idx) => (
              <Text key={idx}>{`${skill}: ${data.level || data.score}`}</Text>
            ))}
          </View>
        )}

        {/* Hard skills */}
        {extras.hardSkillsResults && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.subtitle}>Habilidades Duras Destacadas</Text>
            {Object.entries(extras.hardSkillsResults).map(([skill, data], idx) => (
              <Text key={idx}>{`${skill}: ${data.level || data.score}`}</Text>
            ))}
          </View>
        )}

        {/* Análisis general */}
        {extras.analysis && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.subtitle}>Experiencia y Logros</Text>
            <Text>{extras.analysis}</Text>
          </View>
        )}

        {/* ======= Entrevista: Pregunta + Respuesta + Análisis + Puntaje ======= */}
        {analysisArr.length > 0 && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.subtitle}>Resultados de Entrevista</Text>

            {analysisArr.map((item, idx) => {
              // Pregunta por índice (como en InterviewResults.js)
              const q = questionsArr[idx];
              const r = responsesArr[idx];

              const questionText = typeof q === "string"
                ? q
                : getField(q || {}, ["text", "question", "label", "title", "content", "value"], "—");

              const userAnswer = typeof r === "string"
                ? r
                : getField(r || {}, ["answer", "response", "text", "value"], "");

              const explanation = getField(item || {}, ["explanation", "analysis", "feedback", "notes", "comment", "reasoning"], "");
              const score = getField(item || {}, ["score", "rating", "value", "points"], "");

              const competence = getField(item || {}, ["competence", "category", "skill"], "");

              return (
                <View key={idx} style={pdfStyles.qaItem}>
                  <Text style={pdfStyles.qaText}>
                    <Text style={pdfStyles.qaLabel}>{idx + 1}. Pregunta: </Text>{String(questionText)}
                  </Text>

                  {competence ? (
                    <Text style={pdfStyles.qaMeta}>Competencia: {String(competence)}</Text>
                  ) : null}

                  {userAnswer ? (
                    <Text style={pdfStyles.qaText}>
                      <Text style={pdfStyles.qaLabel}>Respuesta: </Text>{String(userAnswer)}
                    </Text>
                  ) : null}

                  <Text style={pdfStyles.qaText}>
                    <Text style={pdfStyles.qaLabel}>Análisis: </Text>{explanation || "No disponible."}
                  </Text>

                  {String(score) !== "" ? (
                    <Text style={pdfStyles.scoreTag}>Puntaje: {String(score)}</Text>
                  ) : null}
                </View>
              );
            })}
          </View>
        )}
        {/* ======= Fin Entrevista ======= */}

        {/* Vocacional Secundaria */}
        {extras.vocationalSecundary && extras.vocationalSecundary.bestArea && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.subtitle}>Orientación Vocacional (Secundaria)</Text>
            <Text>Área de mayor afinidad: {extras.vocationalSecundary.bestArea}</Text>
          </View>
        )}

        {/* Vocacional Universidad */}
        {extras.vocationalUniversity && extras.vocationalUniversity.bestArea && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.subtitle}>Orientación Vocacional (Universidad)</Text>
            <Text>Área de mayor afinidad: {extras.vocationalUniversity.bestArea}</Text>
          </View>
        )}

        {/* Bullying */}
        {extras.bullying && extras.bullying.riskLevel && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.subtitle}>Resultado de Bullying</Text>
            <Text>Nivel de riesgo: {extras.bullying.riskLevel}</Text>
            {extras.bullying.description && <Text>{extras.bullying.description}</Text>}
          </View>
        )}

        {/* Adicciones */}
        {extras.addiction && extras.addiction.riskLevel && (
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.subtitle}>Resultado de Detección de Adicciones</Text>
            <Text>Nivel de riesgo: {extras.addiction.riskLevel}</Text>
            {extras.addiction.description && <Text>{extras.addiction.description}</Text>}
          </View>
        )}
      </Page>
    </Document>
  );
};

// ================== Página React ==================
const GeneratedCV = () => {
  const [cv, setCV] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [extras, setExtras] = useState({});
  const [regenerating, setRegenerating] = useState(false);
  const [cvGeneratedAt, setCvGeneratedAt] = useState(null);
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState("cv");

  useEffect(() => {
    const fetchCV = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        // 1) Intentar obtener el CV guardado
        try {
          const savedResponse = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/users/get-cv`,
            { headers: { Authorization: token } }
          );

          setCV(savedResponse.data.cv);
          setCvGeneratedAt(savedResponse.data.cvGeneratedAt);
          setExtras({
            softSkillsResults: savedResponse.data.softSkillsResults,
            hardSkillsResults: savedResponse.data.hardSkillsResults,
            vocationalSecundary: savedResponse.data.vocationalSecundary,
            vocationalUniversity: savedResponse.data.vocationalUniversity,
            interviewAnalysis: savedResponse.data.interviewAnalysis, // lo que ya tenías
            analysis: savedResponse.data.analysis,
            bullying: savedResponse.data.bullying,
            addiction: savedResponse.data.addiction,
          });

          // 1.1) Enriquecer con entrevista completa (questions/responses)
          const interviewFull = await fetchInterviewData(token);
          if (interviewFull) {
            setExtras(prev => ({
              ...prev,
              interview: {
                score: interviewFull.score,
                analysis: interviewFull.analysis || [],
                questions: interviewFull.questions || [],
                responses: interviewFull.responses || []
              }
            }));
          }

          console.log("CV cargado desde la base de datos");
        } catch (savedError) {
          // 2) Si no hay CV guardado, generar uno nuevo
          console.log("No hay CV guardado, generando uno nuevo...");
          const generateResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/users/generate-cv`,
            {},
            { headers: { Authorization: token } }
          );

          setCV(generateResponse.data.cv);
          setCvGeneratedAt(generateResponse.data.cvGeneratedAt);
          setExtras({
            softSkillsResults: generateResponse.data.softSkillsResults,
            hardSkillsResults: generateResponse.data.hardSkillsResults,
            vocationalSecundary: generateResponse.data.vocationalSecundary,
            vocationalUniversity: generateResponse.data.vocationalUniversity,
            interviewAnalysis: generateResponse.data.interviewAnalysis,
            analysis: generateResponse.data.analysis,
            bullying: generateResponse.data.bullying,
            addiction: generateResponse.data.addiction,
          });

          // 2.1) Enriquecer con entrevista completa
          const interviewFull = await fetchInterviewData(token);
          if (interviewFull) {
            setExtras(prev => ({
              ...prev,
              interview: {
                score: interviewFull.score,
                analysis: interviewFull.analysis || [],
                questions: interviewFull.questions || [],
                responses: interviewFull.responses || []
              }
            }));
          }

          console.log("CV generado y guardado en la base de datos");
        }
      } catch (err) {
        setError("No se pudo obtener o generar el CV. Intenta más tarde.");
      } finally {
        setLoading(false);
      }
    };
    fetchCV();
  }, [navigate]);

  const handleDownloadPDF = async () => {
    const blob = await pdf(<CVDocument cv={cv} extras={extras} />).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Curriculum_Profesional.pdf";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRegenerateCV = async () => {
    setRegenerating(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/generate-cv`,
        {},
        { headers: { Authorization: token } }
      );

      setCV(response.data.cv);
      setCvGeneratedAt(response.data.cvGeneratedAt);
      setExtras({
        softSkillsResults: response.data.softSkillsResults,
        hardSkillsResults: response.data.hardSkillsResults,
        vocationalSecundary: response.data.vocationalSecundary,
        vocationalUniversity: response.data.vocationalUniversity,
        interviewAnalysis: response.data.interviewAnalysis,
        analysis: response.data.analysis,
        bullying: response.data.bullying,
        addiction: response.data.addiction,
      });

      // Re-enriquecer con entrevista completa tras regenerar
      const interviewFull = await fetchInterviewData(token);
      if (interviewFull) {
        setExtras(prev => ({
          ...prev,
          interview: {
            score: interviewFull.score,
            analysis: interviewFull.analysis || [],
            questions: interviewFull.questions || [],
            responses: interviewFull.responses || []
          }
        }));
      }

      console.log("CV regenerado y guardado en la base de datos");
    } catch (err) {
      setError("No se pudo regenerar el CV. Intenta más tarde.");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="dashboard-layout">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          <div className="sidebar-header">
            <h3>CV Generado</h3>
          </div>
          <div className="sidebar-menu">
            <button
              className={`menu-item ${activeSection === "cv" ? "active" : ""}`}
              onClick={() => setActiveSection("cv")}
            >
              <i className="bi bi-file-text"></i> CV Generado
            </button>
            <button
              className="menu-item return-dashboard"
              onClick={() => navigate("/dashboard")}
            >
              <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          <div className="generated-cv-container" style={{ padding: 0, maxWidth: '100%' }}>
            <div className="cv-page-header">
              <div className="header-content">
                <div className="header-left">
                  <img src={logo} alt="Logo" className="header-logo" />
                  <h2>CV Generado por IA</h2>
                </div>
                <div className="header-right">
                </div>
              </div>
            </div>
            <div className="cv-main-content">
              {loading ? (
                <div className="loading-container">
                  <div className="loading-animation">
                    <div className="loading-circle"></div>
                    <div className="loading-circle"></div>
                    <div className="loading-circle"></div>
                  </div>
                  <p className="loading-text">Generando tu CV...</p>
                </div>
              ) : error ? (
                <div className="error-container">
                  <i className="bi bi-exclamation-circle-fill"></i>
                  <h3>Error</h3>
                  <p>{error}</p>
                  <button onClick={() => navigate("/dashboard")} className="btn btn-primary">
                    Volver al Dashboard
                  </button>
                </div>
              ) : (
                <div className="cv-display-container">
                  <h2 className="cv-title">Currículum Profesional</h2>
                  {cvGeneratedAt && (
                    <div className="cv-generation-info">
                      <small>
                        <i className="bi bi-clock"></i>
                        Generado el: {new Date(cvGeneratedAt).toLocaleString('es-ES')}
                      </small>
                    </div>
                  )}
                  <div className="cv-plain-text">{cv}</div>

                  {/* Botones */}
                  <div className="cv-actions">
                    <button className="btn btn-primary" onClick={handleDownloadPDF}>
                      <i className="bi bi-file-earmark-arrow-down"></i> Descargar PDF
                    </button>
                    <button
                      className="btn btn-warning"
                      onClick={handleRegenerateCV}
                      disabled={regenerating}
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                      {regenerating ? "Regenerando..." : "Regenerar CV"}
                    </button>
                    <button className="btn btn-secondary" onClick={() => navigate("/dashboard")}>
                      <i className="bi bi-arrow-left"></i> Volver al Dashboard
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneratedCV;